export type History<T> = {
  past: T[]
  present: T
  future: T[]
}

const isSame = <T>(a: T, b: T) => a === b || JSON.stringify(a) === JSON.stringify(b)

export const createHistory = <T>(present: T): History<T> => ({ past: [], present, future: [] })

/**
 * Records `next` as a new step, keeping at most `limit` past steps. A new step clears redo.
 * Unless `force`, a step identical to the present one is ignored.
 */
export const pushHistory = <T>(
  history: History<T>,
  next: T,
  { limit, force = false }: { limit: number; force?: boolean },
): History<T> => {
  if (!force && isSame(history.present, next)) return history

  return {
    past: [...history.past, history.present].slice(-limit),
    present: next,
    future: [],
  }
}

export const undoHistory = <T>(history: History<T>): History<T> => {
  if (history.past.length === 0) return history

  return {
    past: history.past.slice(0, -1),
    present: history.past[history.past.length - 1],
    future: [history.present, ...history.future],
  }
}

export const redoHistory = <T>(history: History<T>): History<T> => {
  if (history.future.length === 0) return history

  return {
    past: [...history.past, history.present],
    present: history.future[0],
    future: history.future.slice(1),
  }
}
